/**
 * BandwidthAnalytics page — real-time bandwidth utilization from Meraki data.
 *
 * Shows: Top utilized sites, site-level trend lookup, carrier distribution,
 * and business hours utilization alerts based on actual traffic data.
 */

import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { apiClient } from '@/services/apiClient';
import { bandwidthService } from '@/services/bandwidthService';
import type { CarrierDistributionItem } from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────

interface UtilizationSite {
  siteNumber: string;
  branchName: string;
  avgMbps: number;
  peakMbps: number;
  sampleCount: number;
  circuitBandwidthMbps: number | null;
  utilizationPercent: number | null;
}

interface UtilizationSummary {
  totalSitesMonitored: number;
  sitesWithData: number;
  avgUtilizationMbps: number;
  peakUtilizationMbps: number;
  topSites: UtilizationSite[];
  lastUpdated: string | null;
}

interface SiteTrendEntry {
  sampleTime: string;
  totalMbps: number;
  wan1Mbps: number;
  wan2Mbps: number;
}

interface BandwidthAnomaly {
  siteNumber: string;
  branchName: string;
  abcCategory: string | null;
  totalBandwidthMbps: number | null;
  avgUtilizationMbps: number | null;
  peakUtilizationMbps: number | null;
  utilizationPercent: number | null;
  severity: 'critical' | 'warning' | 'info';
  reasons: string[];
  recommendation: string;
}

interface AnomalyResponse {
  totalSitesAnalyzed: number;
  anomalyCount: number;
  critical: number;
  warning: number;
  info: number;
  anomalies: BandwidthAnomaly[];
  businessHours: string;
  generatedAt: string;
}

// ─── Utilization Summary Section ───────────────────────────────────────

type SortKey = 'siteNumber' | 'branchName' | 'avgMbps' | 'peakMbps' | 'circuitBandwidthMbps' | 'utilizationPercent' | 'sampleCount';

function UtilizationSummarySection() {
  const [sortBy, setSortBy] = useState<SortKey>('avgMbps');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const { data, isLoading, error } = useQuery({
    queryKey: ['bandwidth-utilization-summary'],
    queryFn: () => apiClient.get<UtilizationSummary>('bandwidth-utilization/summary'),
  });

  if (isLoading) return <LoadingSpinner message="Loading utilization data…" />;
  if (error) return <ErrorMessage message={(error as Error).message ?? 'Failed to load utilization data.'} />;
  if (!data) return null;

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortedSites = [...(data.topSites || [])].sort((a, b) => {
    const aVal = a[sortBy] ?? 0;
    const bVal = b[sortBy] ?? 0;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
  });

  const SortHeader = ({ label, field, align }: { label: string; field: SortKey; align?: 'left' | 'right' }) => (
    <th
      className={`py-2 px-2 font-medium text-slate-600 cursor-pointer hover:text-baker-red select-none ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => handleSort(field)}
    >
      {label} {sortBy === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  return (
    <div className="space-y-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Sites Monitored" value={data.totalSitesMonitored} />
        <StatCard label="Sites with Data" value={data.sitesWithData} />
        <StatCard label="Avg Utilization" value={data.avgUtilizationMbps > 0 ? `${data.avgUtilizationMbps.toFixed(1)} Mbps` : '—'} />
        <StatCard label="Peak Utilization" value={data.peakUtilizationMbps > 0 ? `${data.peakUtilizationMbps.toFixed(1)} Mbps` : '—'} />
      </div>

      {data.lastUpdated && (
        <p className="text-xs text-slate-400">Last data: {new Date(data.lastUpdated).toLocaleString()}</p>
      )}

      {/* All Utilized Sites — scrollable, sortable */}
      {sortedSites.length > 0 && (
        <div className="bg-white rounded-lg border border-border p-5">
          <h2 className="text-base font-semibold text-text-primary mb-3">Site Utilization (Business Hours) — {sortedSites.length} sites</h2>
          <div className="overflow-y-auto max-h-[500px] overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b-2 border-border">
                  <SortHeader label="Site" field="siteNumber" align="left" />
                  <SortHeader label="Branch" field="branchName" align="left" />
                  <SortHeader label="Avg Mbps" field="avgMbps" align="right" />
                  <SortHeader label="Peak Mbps" field="peakMbps" align="right" />
                  <SortHeader label="Circuit Capacity" field="circuitBandwidthMbps" align="right" />
                  <SortHeader label="% Used" field="utilizationPercent" align="right" />
                  <SortHeader label="Samples" field="sampleCount" align="right" />
                </tr>
              </thead>
              <tbody>
                {sortedSites.map((site) => (
                  <tr key={site.siteNumber} className="border-b border-border-subtle hover:bg-page/50">
                    <td className="py-2 px-2">
                      <a href={`/sites/${site.siteNumber}`} className="text-baker-red no-underline hover:underline font-medium">
                        {site.siteNumber}
                      </a>
                    </td>
                    <td className="py-2 px-2">{site.branchName}</td>
                    <td className="py-2 px-2 text-right font-medium">{site.avgMbps.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right">{site.peakMbps.toFixed(2)}</td>
                    <td className="py-2 px-2 text-right">{site.circuitBandwidthMbps ? `${site.circuitBandwidthMbps} Mbps` : '—'}</td>
                    <td className="py-2 px-2 text-right">
                      {site.utilizationPercent != null ? (
                        <span className={`font-medium ${site.utilizationPercent > 80 ? 'text-red-600' : site.utilizationPercent > 60 ? 'text-amber-600' : 'text-green-600'}`}>
                          {site.utilizationPercent.toFixed(0)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-2 text-right text-slate-500">{site.sampleCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.sitesWithData === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-700 text-sm">
          No bandwidth utilization data collected yet. The Meraki bandwidth monitor runs every 15 minutes during business hours (7am–5pm M-F). Data will appear after the next collection cycle.
        </div>
      )}
    </div>
  );
}

// ─── Site Trend Section ────────────────────────────────────────────────

function SiteTrendSection() {
  const [siteNumber, setSiteNumber] = useState('');
  const [querySite, setQuerySite] = useState('');

  const { data: trend, isLoading, error } = useQuery({
    queryKey: ['bandwidth-site-trend', querySite],
    queryFn: () => apiClient.get<SiteTrendEntry[]>(`bandwidth-utilization/trend/${querySite}`),
    enabled: !!querySite && /^\d{3}$/.test(querySite),
  });

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (/^\d{3}$/.test(siteNumber)) {
      setQuerySite(siteNumber);
    }
  }, [siteNumber]);

  return (
    <div className="bg-white rounded-lg border border-border p-5">
      <h2 className="text-base font-semibold text-text-primary mb-3">Site Bandwidth Trend</h2>

      <form onSubmit={handleSubmit} className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Site # (e.g., 001)"
          value={siteNumber}
          onChange={(e) => setSiteNumber(e.target.value)}
          maxLength={3}
          className="py-2 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40 w-32"
        />
        <button
          type="submit"
          disabled={!/^\d{3}$/.test(siteNumber)}
          className="px-4 py-2 rounded-lg bg-baker-red text-white text-sm font-medium disabled:bg-slate-300 disabled:cursor-not-allowed hover:bg-baker-red/90"
        >
          View Trend
        </button>
      </form>

      {isLoading && <LoadingSpinner message="Loading trend data…" />}
      {error && <ErrorMessage message={(error as Error).message ?? 'Failed to load trend.'} />}

      {trend && trend.length === 0 && querySite && (
        <p className="text-slate-500 text-sm">No bandwidth data found for site {querySite}. Data is collected during business hours only.</p>
      )}

      {trend && trend.length > 0 && (
        <div className="overflow-x-auto">
          <p className="text-xs text-slate-400 mb-2">Last {trend.length} samples (most recent first)</p>
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b-2 border-border">
                <th className="text-left py-2 px-2 font-medium text-slate-600">Time</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">WAN1 (Mbps)</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">WAN2 (Mbps)</th>
                <th className="text-right py-2 px-2 font-medium text-slate-600">Total (Mbps)</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((entry, idx) => (
                <tr key={idx} className="border-b border-border-subtle">
                  <td className="py-1.5 px-2">{new Date(entry.sampleTime).toLocaleString()}</td>
                  <td className="py-1.5 px-2 text-right">{entry.wan1Mbps.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-right">{entry.wan2Mbps.toFixed(2)}</td>
                  <td className="py-1.5 px-2 text-right font-medium">{entry.totalMbps.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Carrier Distribution Section ──────────────────────────────────────

function CarrierDistributionSection() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bandwidth-carrier-distribution'],
    queryFn: () => bandwidthService.getCarrierDistribution(),
  });

  return (
    <div className="bg-white rounded-lg border border-border p-5">
      <h2 className="text-base font-semibold text-text-primary mb-3">Carrier Distribution</h2>

      {isLoading && <LoadingSpinner message="Loading carrier data…" />}
      {error && <ErrorMessage message={(error as Error).message ?? 'Failed to load carrier distribution.'} />}

      {data && data.length === 0 && (
        <p className="text-slate-500 text-sm">No carrier data available.</p>
      )}

      {data && data.length > 0 && (
        <div className="space-y-2">
          {data.map((item: CarrierDistributionItem) => {
            const maxCount = Math.max(...data.map((d: CarrierDistributionItem) => d.circuitCount), 1);
            const pct = (item.circuitCount / maxCount) * 100;
            return (
              <div key={item.carrier}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium text-slate-700">{item.carrier}</span>
                  <span className="text-slate-500">{item.circuitCount} circuits</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className="h-2 rounded-full bg-baker-red" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Business Hours Utilization Alerts ─────────────────────────────────

function BusinessHoursAlertsSection() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['bandwidth-business-hours-alerts'],
    queryFn: () => apiClient.get<AnomalyResponse>('bandwidth-snapshots/analytics/anomalies'),
  });

  return (
    <div className="bg-white rounded-lg border border-border p-5">
      <h2 className="text-base font-semibold text-text-primary mb-1">Capacity Alerts</h2>
      <p className="text-xs text-slate-500 mb-3">
        Sites where utilization approaches circuit capacity during business hours (7am–5pm M-F).
      </p>

      {isLoading && <LoadingSpinner message="Loading alerts…" />}
      {error && <ErrorMessage message={(error as Error).message ?? 'Failed to load alerts.'} />}

      {data && data.anomalyCount === 0 && data.totalSitesAnalyzed > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700 text-sm">
          ✓ All {data.totalSitesAnalyzed} monitored sites are within acceptable utilization levels.
        </div>
      )}

      {data && data.totalSitesAnalyzed === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-700 text-sm">
          No utilization data available yet. The bandwidth monitor collects data every 15 minutes during business hours.
        </div>
      )}

      {data && data.anomalies.length > 0 && (
        <div>
          <div className="flex gap-3 mb-3 text-xs">
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">{data.critical} critical</span>
            <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">{data.warning} warning</span>
            <span className="text-slate-500">{data.totalSitesAnalyzed} sites analyzed</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Site</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Branch</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Avg Mbps</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">Peak Mbps</th>
                  <th className="text-right py-2 px-2 font-medium text-slate-600">% Capacity</th>
                  <th className="text-center py-2 px-2 font-medium text-slate-600">Severity</th>
                  <th className="text-left py-2 px-2 font-medium text-slate-600">Recommendation</th>
                </tr>
              </thead>
              <tbody>
                {data.anomalies.map((item) => (
                  <tr key={item.siteNumber} className={`border-b border-border-subtle ${item.severity === 'critical' ? 'bg-red-50' : item.severity === 'warning' ? 'bg-amber-50' : ''}`}>
                    <td className="py-2 px-2">
                      <a href={`/sites/${item.siteNumber}`} className="text-baker-red no-underline hover:underline font-medium">
                        {item.siteNumber}
                      </a>
                    </td>
                    <td className="py-2 px-2">{item.branchName}</td>
                    <td className="py-2 px-2 text-right">{item.avgUtilizationMbps?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-2 text-right">{item.peakUtilizationMbps?.toFixed(1) ?? '—'}</td>
                    <td className="py-2 px-2 text-right font-medium">
                      {item.utilizationPercent != null ? `${item.utilizationPercent}%` : '—'}
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        item.severity === 'warning' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {item.severity}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs text-slate-600">{item.recommendation}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat Card ─────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg border border-border p-4">
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-text-primary mt-1">{value}</div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────

export function BandwidthAnalytics() {
  return (
    <div>
      <h1 className="text-lg font-semibold text-text-primary mb-6">Bandwidth Analytics</h1>

      {/* Utilization Summary + Top Sites */}
      <div className="mb-6">
        <UtilizationSummarySection />
      </div>

      {/* Two-column: Site Trend + Carrier Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <SiteTrendSection />
        <CarrierDistributionSection />
      </div>

      {/* Capacity Alerts */}
      <div className="mb-6">
        <BusinessHoursAlertsSection />
      </div>
    </div>
  );
}
